import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { LucideAngularModule, Lightbulb, X, ChevronRight } from 'lucide-angular';
import { LanguageService } from '@core';

/** Localized copy for each stepper tour step. */
const COPY: Record<string, Record<string, string>> = {
  en: {
    label:   'Tip {{step}} of {{total}}',
    skip:    'Skip tour',
    got_it:  'Got it',
    next:    'Next',
    dont_show: "Don't show this tour again",
    t1: 'Pick your AI categories',
    d1: 'Choose up to 3 trade scopes — flooring, drywall, paint, HVAC, plumbing, or structural. The AI will analyze only what you select.',
    t2: 'Upload your floor plan',
    d2: 'Drop in a PDF or image. Multi-page documents work — each page becomes a separate floor in the next step.',
    t3: 'Assign floors & set scale',
    d3: 'Drag page thumbnails into floor slots on the left, then click a scale bar on the plan to calibrate real-world measurements.',
    t4: 'Review & edit AI results',
    d4: 'Inspect detected rooms, refine polygon boundaries in edit mode, then open the pricing drawer to generate cost estimates.',
  },
  es: {
    label:   'Consejo {{step}} de {{total}}',
    skip:    'Saltar tour',
    got_it:  'Entendido',
    next:    'Siguiente',
    dont_show: 'No mostrar este tour de nuevo',
    t1: 'Elige tus categorías de IA',
    d1: 'Selecciona hasta 3 ámbitos — pisos, paneles de yeso, pintura, HVAC, plomería o estructural. La IA solo analizará lo que elijas.',
    t2: 'Sube tu plano de planta',
    d2: 'Arrastra un PDF o imagen. Los documentos de varias páginas son compatibles — cada página se convierte en un piso en el siguiente paso.',
    t3: 'Asigna pisos y define escala',
    d3: 'Arrastra las miniaturas a los slots de piso a la izquierda, luego haz clic en la barra de escala del plano para calibrar las medidas.',
    t4: 'Revisa y edita los resultados',
    d4: 'Inspecciona las habitaciones detectadas, ajusta los bordes en modo edición y abre el panel de precios para generar estimaciones.',
  },
  fr: {
    label:   'Conseil {{step}} sur {{total}}',
    skip:    'Ignorer',
    got_it:  'Compris',
    next:    'Suivant',
    dont_show: 'Ne plus afficher ce tour',
    t1: 'Choisissez vos catégories IA',
    d1: 'Sélectionnez jusqu\'à 3 corps de métier — revêtement, cloisons, peinture, CVC, plomberie ou structure. L\'IA analysera uniquement votre sélection.',
    t2: 'Téléversez votre plan',
    d2: 'Déposez un PDF ou une image. Les documents multipage sont pris en charge — chaque page devient un étage à l\'étape suivante.',
    t3: 'Assignez les étages et l\'échelle',
    d3: 'Faites glisser les vignettes vers les slots d\'étage à gauche, puis cliquez sur la barre d\'échelle pour calibrer les mesures.',
    t4: 'Révisez et modifiez les résultats',
    d4: 'Inspectez les pièces détectées, affinez les contours en mode édition, puis ouvrez le volet de tarification pour générer des estimations.',
  },
};

/** localStorage key used to remember a permanent tour opt-out across sessions. */
const TOUR_DISMISSED_KEY = 'stepper_tour_dismissed';

/**
 * Guided tour overlay that explains each project-stepper stage once per session.
 */
@Component({
  selector: 'app-stepper-tour',
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .tour-backdrop {
      position: fixed; inset: 0; z-index: 509;
      background: rgb(var(--talos-ink-rgb) / 0.50);
      animation: tour-fade 0.22s ease;
    }
    .tour-card {
      position: fixed; top: 68px; left: 50%; transform: translateX(-50%);
      z-index: 510; width: 348px;
      background: var(--talos-bg); border-radius: 6px;
      padding: 20px 20px 16px;
      box-shadow: 0 24px 64px rgb(var(--talos-ink-rgb) / 0.16), 0 0 0 1px rgb(var(--talos-ink-rgb) / 0.08);
      border-left: 3px solid var(--talos-accent);
      animation: tour-drop 0.28s cubic-bezier(0.34,1.4,0.64,1);
    }
    .tour-header {
      display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;
    }
    .tour-label {
      font-family: var(--font-mono, monospace);
      font-size: 0.56rem; font-weight: 700; letter-spacing: 0.16em;
      text-transform: uppercase; color: rgb(var(--talos-accent-rgb) / 0.60);
    }
    .tour-close {
      background: none; border: none; cursor: pointer; padding: 3px;
      color: rgb(var(--talos-ink-rgb) / 0.28); border-radius: 3px; display: flex; align-items: center;
      transition: color 0.15s, background 0.15s;
    }
    .tour-close:hover { color: rgb(var(--talos-ink-rgb) / 0.60); background: rgb(var(--talos-ink-rgb) / 0.05); }
    .tour-icon {
      width: 34px; height: 34px; border-radius: 5px;
      background: rgb(var(--talos-accent-rgb) / 0.07); border: 1px solid rgb(var(--talos-accent-rgb) / 0.14);
      display: flex; align-items: center; justify-content: center; margin-bottom: 12px;
    }
    .tour-title {
      font-size: 0.92rem; font-weight: 700; color: var(--talos-ink);
      margin: 0 0 7px; line-height: 1.25;
    }
    .tour-desc {
      font-size: 0.76rem; color: rgb(var(--talos-ink-rgb) / 0.52); line-height: 1.68; margin: 0 0 18px;
    }
    .tour-dont-show {
      display: flex; align-items: center; gap: 6px; margin-bottom: 14px;
      cursor: pointer; user-select: none;
    }
    .tour-dont-show input[type="checkbox"] {
      width: 13px; height: 13px; cursor: pointer; accent-color: var(--talos-accent);
    }
    .tour-dont-show span {
      font-size: 0.70rem; color: rgb(var(--talos-ink-rgb) / 0.50);
    }
    .tour-actions {
      display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
    }
    .tour-skip {
      background: none; border: none; cursor: pointer;
      font-size: 0.70rem; color: rgb(var(--talos-ink-rgb) / 0.28); padding: 0;
      transition: color 0.15s; font-family: var(--font-mono, monospace); letter-spacing: 0.04em;
    }
    .tour-skip:hover { color: rgb(var(--talos-ink-rgb) / 0.55); }
    .tour-next {
      display: inline-flex; align-items: center; gap: 4px;
      background: var(--talos-ink); color: var(--talos-bg); border: none; border-radius: 4px;
      padding: 7px 16px; font-size: 0.74rem; font-weight: 700; cursor: pointer;
      transition: background 0.15s; letter-spacing: 0.04em;
    }
    .tour-next:hover { background: var(--talos-accent); }
    .tour-dots {
      display: flex; align-items: center; gap: 5px; justify-content: center;
    }
    .tour-dot {
      height: 5px; border-radius: 3px;
      background: rgb(var(--talos-ink-rgb) / 0.12); transition: all 0.22s ease;
      width: 5px;
    }
    .tour-dot.active { background: var(--talos-accent); width: 16px; }
    .tour-dot.done  { background: rgb(var(--talos-accent-rgb) / 0.30); }
    @keyframes tour-fade  { from { opacity: 0 } to { opacity: 1 } }
    @keyframes tour-drop  {
      from { opacity: 0; transform: translateX(-50%) translateY(-14px) scale(0.97); }
      to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
    }
  `],
  template: `
    @if (visible()) {
      <div class="tour-backdrop" (click)="onSkip()"></div>
      <div class="tour-card" role="status" aria-live="polite">
        <div class="tour-header">
          <span class="tour-label">{{ label() }}</span>
          <button class="tour-close" [title]="c()['skip']" (click)="onSkip()">
            <lucide-icon [img]="XIcon" [size]="11" />
          </button>
        </div>
        <div class="tour-icon">
          <lucide-icon [img]="LightbulbIcon" [size]="15" color="var(--talos-accent)" />
        </div>
        <h3 class="tour-title">{{ title() }}</h3>
        <p class="tour-desc">{{ desc() }}</p>
        <label class="tour-dont-show">
          <input
            type="checkbox"
            [checked]="dontShowAgain()"
            (change)="onDontShowAgainChange($any($event.target).checked)"
          />
          <span>{{ c()['dont_show'] }}</span>
        </label>
        <div class="tour-actions">
          <button class="tour-skip" (click)="onSkip()">{{ c()['skip'] }}</button>
          <button class="tour-next" (click)="onGotIt()">
            {{ isLast() ? c()['got_it'] : c()['next'] }}
            @if (!isLast()) { <lucide-icon [img]="ChevronRightIcon" [size]="11" /> }
          </button>
        </div>
        <div class="tour-dots">
          @for (s of stepsRange; track s) {
            <span
              class="tour-dot"
              [class.active]="s === currentStep()"
              [class.done]="s < currentStep()"
            ></span>
          }
        </div>
      </div>
    }
  `,
})
export class StepperTourComponent {
  /** Current one-based workflow step shown by the parent stepper. */
  readonly currentStep = input.required<number>();
  /** Emits when a single visible tip is dismissed. */
  readonly dismissed   = output<void>();
  /** Emits when the full tour is skipped or all tips have been seen. */
  readonly allDone     = output<void>();

  /** Step indexes rendered as progress dots. */
  readonly stepsRange = [1, 2, 3, 4];
  /** Total number of tips in the tour. */
  private readonly totalSteps = 4;

  /** Header icon for the tour card. */
  readonly LightbulbIcon    = Lightbulb;
  /** Close button icon. */
  readonly XIcon            = X;
  /** Next button icon. */
  readonly ChevronRightIcon = ChevronRight;

  /** Active language state used to choose localized tour copy. */
  private readonly lang = inject(LanguageService);

  /** Whether the tour can currently display tips. Starts disabled if the user previously opted out for good. */
  private readonly _active    = signal(localStorage.getItem(TOUR_DISMISSED_KEY) !== 'true');
  /** Workflow steps already acknowledged in this session. */
  private readonly _seen      = signal<Set<number>>(new Set());
  /** Whether the "don't show again" checkbox is checked. */
  readonly dontShowAgain = signal(false);

  /** Whether the current tip should be visible. */
  readonly visible = computed(() => {
    this.lang.currentLang();
    return this._active() && !this._seen().has(this.currentStep());
  });

  /** Whether the current tip is the final tour step. */
  readonly isLast = computed(() => this.currentStep() === this.totalSteps);

  /** Localized copy map for the active language. */
  readonly c = computed(() => {
    const l = this.lang.currentLang() as string;
    return COPY[l] ?? COPY['en'];
  });

  /** Progress label with current and total step numbers interpolated. */
  readonly label = computed(() => {
    const raw = this.c()['label'];
    return raw.replace('{{step}}', String(this.currentStep())).replace('{{total}}', String(this.totalSteps));
  });

  /** Localized title for the current step. */
  readonly title = computed(() => {
    const c = this.c();
    const map: Record<number, string> = { 1: c['t1'], 2: c['t2'], 3: c['t3'], 4: c['t4'] };
    return map[this.currentStep()] ?? '';
  });

  /** Localized description for the current step. */
  readonly desc = computed(() => {
    const c = this.c();
    const map: Record<number, string> = { 1: c['d1'], 2: c['d2'], 3: c['d3'], 4: c['d4'] };
    return map[this.currentStep()] ?? '';
  });

  /** Marks the current tip as seen and completes the tour when all steps are done. */
  onGotIt(): void {
    this._seen.update(s => new Set([...s, this.currentStep()]));
    this.dismissed.emit();
    if (this._seen().size >= this.totalSteps) {
      this._active.set(false);
      this.allDone.emit();
    }
  }

  /** Skips the remaining tour tips and persists the dismissal so it never reappears. */
  onSkip(): void {
    localStorage.setItem(TOUR_DISMISSED_KEY, 'true');
    this.dontShowAgain.set(true);
    this._active.set(false);
    this.allDone.emit();
  }

  /** Checking the box immediately skips the tour for good, across future sessions. */
  onDontShowAgainChange(checked: boolean): void {
    this.dontShowAgain.set(checked);
    if (checked) {
      localStorage.setItem(TOUR_DISMISSED_KEY, 'true');
      this._active.set(false);
      this.allDone.emit();
    }
  }

  /** Re-enables the tour from the current step, clearing any permanent opt-out. Called by a parent "replay tour" action. */
  restart(): void {
    localStorage.removeItem(TOUR_DISMISSED_KEY);
    this.dontShowAgain.set(false);
    this._seen.set(new Set());
    this._active.set(true);
  }
}
